import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSchedule } from '../context/ScheduleContext';

// ─── Types ─────────────────────────────────────────────────────────────────

type MessageRole = 'user' | 'assistant' | 'error';

interface ChatMessage {
  id: number;
  role: MessageRole;
  text: string;
}

let msgCounter = 0;

// ─── Icons ─────────────────────────────────────────────────────────────────

function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
    </svg>
  );
}

// ─── Thinking animation ────────────────────────────────────────────────────

function ThinkingBubble() {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 4 }}>
      <div style={{
        padding: '10px 14px',
        borderRadius: '14px 14px 14px 2px',
        background: '#f1f5f9',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
      }}>
        <style>{`
          @keyframes fs-ai-bounce {
            0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
            40% { transform: translateY(-5px); opacity: 1; }
          }
        `}</style>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#94a3b8',
              display: 'inline-block',
              animation: `fs-ai-bounce 1.2s ${i * 0.2}s infinite ease-in-out`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Single chat bubble ────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === 'user';
  const isError = msg.role === 'error';

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 6,
    }}>
      {!isUser && (
        <div style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: isError ? '#fef2f2' : '#eff6ff',
          border: `1px solid ${isError ? '#fca5a5' : '#bfdbfe'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginRight: 6,
          marginTop: 2,
          color: isError ? '#ef4444' : '#3b82f6',
        }}>
          <SparkleIcon />
        </div>
      )}
      <div style={{
        maxWidth: '82%',
        padding: '8px 12px',
        borderRadius: isUser ? '14px 14px 2px 14px' : '14px 14px 14px 2px',
        fontSize: 12,
        lineHeight: 1.55,
        color: isUser ? '#fff' : isError ? '#dc2626' : '#1e293b',
        background: isUser
          ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
          : isError
          ? '#fef2f2'
          : '#f1f5f9',
        border: isError ? '1px solid #fca5a5' : isUser ? 'none' : '1px solid #e2e8f0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        boxShadow: isUser ? '0 1px 3px rgba(37,99,235,0.25)' : '0 1px 2px rgba(0,0,0,0.05)',
      }}>
        {msg.text}
      </div>
    </div>
  );
}

// ─── Suggestions ───────────────────────────────────────────────────────────

const DEFAULT_SUGGESTIONS = [
  'Show afternoon only (1pm–5pm)',
  'Switch to agenda view',
  'Show morning only (8am–noon)',
  'Reset to business hours',
];

function SuggestionChips({ prompts, onSelect }: { prompts: string[]; onSelect: (text: string) => void }) {
  return (
    <div style={{ padding: '8px 10px 4px', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
      {prompts.map((s) => (
        <button
          key={s}
          onClick={() => onSelect(s)}
          style={{
            fontSize: 10,
            padding: '3px 8px',
            borderRadius: 12,
            border: '1px solid #bfdbfe',
            background: '#eff6ff',
            color: '#2563eb',
            cursor: 'pointer',
            lineHeight: 1.4,
            transition: 'background 0.12s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#dbeafe'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#eff6ff'; }}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────

const DEFAULT_GREETING =
  "Hi! I can adjust the schedule view for you.\n\nTry asking things like:\n• \"Show Dr. Frazier's next 5 Fridays\"\n• \"Afternoon only (1pm–5pm)\"\n• \"Switch to week view\"\n• \"Show only Dr. Smith\"";

export function AiChatPanel() {
  const { config, callbacks, emit, setCurrentDate, setPendingNewEventRequest, applyAcuityConfig, providerMatching, matchingHandle, noShowRiskHandle } = useSchedule();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: ++msgCounter,
      role: 'assistant',
      text: config.aiChatPanel?.greeting ?? DEFAULT_GREETING,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = useCallback(async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { id: ++msgCounter, role: 'user', text: trimmed }]);
    setLoading(true);

    try {
      if (!callbacks.onAiMessage) {
        throw new Error('onAiMessage callback not configured.');
      }
      // Build history: skip the initial greeting (index 0), drop error bubbles.
      // The Anthropic API requires messages to start with 'user', so we find the
      // first user message and send everything from there onward.
      const allHistory = messages
        .filter(m => m.role !== 'error')
        .map(m => ({ role: m.role as 'user' | 'assistant', content: m.text }));
      const firstUser = allHistory.findIndex(m => m.role === 'user');
      const conversationHistory = firstUser >= 0 ? allHistory.slice(firstUser) : [];

      const response = await callbacks.onAiMessage(trimmed, { currentConfig: config, conversationHistory });
      if (response.navigateTo) {
        setCurrentDate(new Date(response.navigateTo));
      }
      if (response.createEvent) {
        const req = response.createEvent;
        // Defer by one tick so any host-side re-render triggered inside onAiMessage
        // (e.g. resource-filter change → renderSchedule()) fully settles first.
        setTimeout(() => setPendingNewEventRequest(req), 0);
      }
      if (response.acuityCommand && config.acuitySlots?.enabled) {
        emit({ type: 'ACUITY_COMMAND', payload: response.acuityCommand });
        const cmdResult = config.acuitySlots.onAcuityCommand?.(response.acuityCommand);
        if (cmdResult && typeof (cmdResult as Promise<unknown>).then === 'function') {
          (cmdResult as Promise<import('../types').AcuityReservation[]>)
            .then(applyAcuityConfig)
            .catch(() => {});
        }
      }
      if (response.matchingCommand && providerMatching?.enabled) {
        // CLEAR intent → clear matching state directly without asking the host.
        if (response.matchingCommand.intent === 'CLEAR') {
          matchingHandle.clearMatching();
        } else {
          emit({ type: 'MATCHING_COMMAND', payload: response.matchingCommand });
          const cmdResult = providerMatching.onMatchingCommand?.(response.matchingCommand);
          if (cmdResult && typeof (cmdResult as Promise<unknown>).then === 'function') {
            (cmdResult as Promise<import('../matching/matchingTypes').MatchingResultsPayload>)
              .then(matchingHandle.applyMatchingResults)
              .catch(() => {});
          }
        }
      }
      if (response.riskCommand && noShowRiskHandle.enabled) {
        noShowRiskHandle.handleRiskCommand(response.riskCommand);
      }
      setMessages((prev) => [...prev, { id: ++msgCounter, role: 'assistant', text: response.reply }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setMessages((prev) => [...prev, { id: ++msgCounter, role: 'error', text: msg }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [loading, callbacks, config, setCurrentDate, setPendingNewEventRequest, providerMatching, matchingHandle, noShowRiskHandle, emit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  }, [input, send]);

  const hasCallback = !!callbacks.onAiMessage;

  if (!hasCallback) {
    return (
      <div style={{ padding: '20px 14px', fontSize: 12, color: '#94a3b8', textAlign: 'center', lineHeight: 1.7 }}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>✨</div>
        <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 6 }}>AI Assistant not configured</div>
        <div>Add your Claude API key and enable <code>onAiMessage</code> in the host app to activate natural language schedule control.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Message list */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '12px 10px 6px',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
        {loading && <ThinkingBubble />}
        <div ref={bottomRef} />
      </div>

      {/* Suggestion chips — shown when no messages beyond the greeting */}
      {messages.length === 1 && !loading && (
        <SuggestionChips
          prompts={config.aiChatPanel?.suggestedPrompts ?? DEFAULT_SUGGESTIONS}
          onSelect={(s) => { setInput(s); inputRef.current?.focus(); }}
        />
      )}

      {/* Input area */}
      <div style={{
        padding: '8px 10px 10px',
        borderTop: '1px solid #e2e8f0',
        flexShrink: 0,
        background: '#f8fafc',
      }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me to change the view…"
            disabled={loading}
            rows={1}
            style={{
              flex: 1,
              padding: '8px 10px',
              fontSize: 12,
              border: '1px solid #cbd5e1',
              borderRadius: 10,
              resize: 'none',
              outline: 'none',
              background: loading ? '#f1f5f9' : '#fff',
              color: '#1e293b',
              lineHeight: 1.5,
              overflowY: 'hidden',
              maxHeight: 90,
              fontFamily: 'inherit',
              transition: 'border-color 0.15s',
            }}
            onFocus={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#3b82f6'; }}
            onBlur={(e) => { (e.currentTarget as HTMLTextAreaElement).style.borderColor = '#cbd5e1'; }}
            onInput={(e) => {
              const el = e.currentTarget as HTMLTextAreaElement;
              el.style.height = 'auto';
              el.style.height = `${Math.min(el.scrollHeight, 90)}px`;
            }}
          />
          <button
            onClick={() => send(input)}
            disabled={loading || !input.trim()}
            title="Send (Enter)"
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              border: 'none',
              cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
              background: loading || !input.trim() ? '#e2e8f0' : '#3b82f6',
              color: loading || !input.trim() ? '#94a3b8' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseEnter={(e) => {
              if (!loading && input.trim()) (e.currentTarget as HTMLButtonElement).style.background = '#2563eb';
            }}
            onMouseLeave={(e) => {
              if (!loading && input.trim()) (e.currentTarget as HTMLButtonElement).style.background = '#3b82f6';
            }}
          >
            <SendIcon />
          </button>
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5, textAlign: 'center' }}>
          Enter to send · Shift+Enter for new line
        </div>
      </div>
    </div>
  );
}
